import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useWorkspace } from "./useWorkspace";

type UseFeatureFlagsOptions = {
  workspaceId?: string | null;
};

type FeatureFlagsState = {
  flags: Record<string, boolean>;
  isEnabled: (flagKey: string) => boolean;
  loading: boolean;
  refresh: () => Promise<void>;
};

export const useFeatureFlags = (options?: UseFeatureFlagsOptions): FeatureFlagsState => {
  const { user, loading: authLoading } = useAuth();
  const { active } = useWorkspace();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);

  const workspaceId = options?.workspaceId ?? active?.id ?? null;

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (authLoading) {
      if (requestId === requestIdRef.current) {
        setLoading(true);
      }
      return;
    }

    if (!user) {
      if (requestId === requestIdRef.current) {
        setFlags({});
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.rpc("get_resolved_feature_flags", {
      _workspace_id: workspaceId ?? undefined,
    });

    if (requestId !== requestIdRef.current) {
      return;
    }

    if (error) {
      setFlags({});
      setLoading(false);
      return;
    }

    const nextFlags = Object.fromEntries((data ?? []).map((item) => [item.flag_key, Boolean(item.enabled)]));
    setFlags(nextFlags);
    setLoading(false);
  }, [authLoading, user, workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => () => {
    requestIdRef.current += 1;
  }, []);

  const isEnabled = useMemo(() => (flagKey: string) => Boolean(flags[flagKey]), [flags]);

  return { flags, isEnabled, loading, refresh };
};
